import React, { FC, useEffect, useState } from "react";
import clsx from "clsx";

import { prefersReducedMotion } from "apps/user/learner/lib/advent-doors";
import { useTranslation } from "lib/i18n/use-translation";

import {
  CompletedNote,
  CompletionError,
  MechanicProps,
  useCompletion,
} from "./shared";
import styles from "../../december-doors.module.scss";

interface BreathMechanicProps extends MechanicProps {
  breaths: number;
  inhaleSeconds: number;
  exhaleSeconds: number;
}

/**
 * Each breath is two steps, in then out: step 2n is breath n+1's inhale and
 * step 2n+1 its exhale.
 */
const BreathMechanic: FC<BreathMechanicProps> = ({
  door,
  breaths,
  inhaleSeconds,
  exhaleSeconds,
  onComplete,
}) => {
  const { t } = useTranslation(["decemberDoors"]);
  const { submit, isSaving, hasFailed } = useCompletion(onComplete);
  const [step, setStep] = useState<number | null>(null);
  const isAnimated = !prefersReducedMotion();

  const isInhale = step !== null && step % 2 === 0;
  const stepSeconds = isInhale ? inhaleSeconds : exhaleSeconds;

  useEffect(() => {
    if (step === null) return undefined;
    const timer = window.setTimeout(() => {
      if (step + 1 < breaths * 2) {
        setStep(step + 1);
        return;
      }
      setStep(null);
      submit({ breaths });
    }, stepSeconds * 1000);
    return () => window.clearTimeout(timer);
  }, [step, stepSeconds, breaths, submit]);

  if (door.isCompleted) {
    return (
      <CompletedNote
        door={door}
        line={t("decemberDoors:mechanic.breath.finished")}
      />
    );
  }

  const isRunning = step !== null;
  const hasFinishedUnsaved = !isRunning && (hasFailed || isSaving);

  return (
    <div className={clsx(styles.mechanic, styles["mechanic-centred"])}>
      <div
        className={styles.breath}
        data-testid="breath-circle"
        data-animated={isAnimated}
      >
        <span
          className={clsx(
            styles["breath-circle"],
            isAnimated && isRunning && isInhale && styles["breath-circle-in"],
            !isAnimated && styles["breath-circle-static"]
          )}
          style={
            isAnimated && isRunning
              ? { transitionDuration: `${stepSeconds}s` }
              : undefined
          }
          aria-hidden
        />
        {isRunning && (
          <span className={styles["breath-phase"]} aria-live="polite">
            {t(
              isInhale
                ? "decemberDoors:mechanic.breath.in"
                : "decemberDoors:mechanic.breath.out"
            )}
          </span>
        )}
      </div>
      {isRunning && (
        <p className={styles["breath-count"]}>
          {t("decemberDoors:mechanic.breath.count", {
            current: Math.floor(step / 2) + 1,
            total: breaths,
          })}
        </p>
      )}
      {!isRunning && !hasFinishedUnsaved && (
        <button
          type="button"
          className={styles["mechanic-primary"]}
          onClick={() => setStep(0)}
        >
          {t("decemberDoors:mechanic.breath.start")}
        </button>
      )}
      {hasFinishedUnsaved && (
        <button
          type="button"
          className={styles["mechanic-primary"]}
          onClick={() => submit({ breaths })}
          disabled={isSaving}
        >
          {t("decemberDoors:mechanic.markDone")}
        </button>
      )}
      <CompletionError show={hasFailed} />
    </div>
  );
};

export default BreathMechanic;
